import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../lib/axiosInstance';
import { RiLayoutGridLine } from 'react-icons/ri';

// Simple Pagination Component
const SimplePagination = ({ currentPage, totalPages, onPageChange, hasNext, hasPrev }) => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pages.push(
            <button
                key={i}
                onClick={() => onPageChange(i)}
                className={`px-3 py-1 rounded text-sm ${i === currentPage
                    ? 'bg-[var(--accent)] text-[var(--accent-contrast)]'
                    : 'bg-[var(--surface)] text-[var(--text)] hover:bg-[var(--border)]'
                    }`}
            >
                {i}
            </button>
        );
    }

    return (
        <div className="flex items-center justify-center gap-2 mt-6">
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={!hasPrev}
                className="px-3 py-1 rounded text-sm bg-[var(--surface)] text-[var(--text)] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--border)]"
            >
                Previous
            </button>
            {pages}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={!hasNext}
                className="px-3 py-1 rounded text-sm bg-[var(--surface)] text-[var(--text)] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--border)]"
            >
                Next
            </button>
        </div>
    );
};

function ProductList() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deletingId, setDeletingId] = useState('');
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        category: '',
        page: 1,
        limit: 10
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalProducts: 0,
        hasNext: false,
        hasPrev: false
    });

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) params.append(key, filters[key]);
            });

            const { data } = await axiosInstance.get(`/brand/all-products?${params}`);
            if (data.success) {
                setProducts(data.data);
                setPagination(data.pagination);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch products');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1 // Reset to first page when filtering
        }));
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({
            ...prev,
            page
        }));
    };

    const formatPrice = (price) => {
        return `₹${price?.toLocaleString('en-IN') || '0'}`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'in stock': return 'bg-green-100 text-green-800';
            case 'out of stock': return 'bg-red-100 text-red-800';
            case 'draft': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const handleDelete = async (productID) => {
        const confirmDelete = window.confirm('Delete this product? This action cannot be undone.');
        if (!confirmDelete) return;
        try {
            setDeletingId(productID);
            setError('');
            await axiosInstance.delete(`/brand/delete/${productID}`);
            await fetchProducts();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete product');
        } finally {
            setDeletingId('');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
                <div className="text-[var(--text)]">Loading products...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--bg)] p-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="lux-heading text-2xl">Products</h1>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/products/reorder"
                            className="px-4 py-2 bg-[var(--surface)] text-[var(--text)] border border-[var(--border)] rounded-lg hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all flex items-center gap-2 text-sm font-semibold shadow-sm"
                        >
                            <RiLayoutGridLine className="w-4 h-4" />
                            Arrange Products
                        </Link>
                        <Link
                            to="/products/add"
                            className="px-4 py-2 bg-[var(--accent)] text-[var(--accent-contrast)] rounded-lg hover:opacity-90 transition-opacity text-sm font-semibold shadow-sm"
                        >
                            Add Product
                        </Link>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-wrap items-center gap-4 mb-6">
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="px-3 py-2 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text)] flex-1 min-w-64"
                    />
                    <select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                        className="px-3 py-2 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text)]"
                    >
                        <option value="">All Status</option>
                        <option value="In Stock">In Stock</option>
                        <option value="Out of Stock">Out of Stock</option>
                        <option value="Draft">Draft</option>
                    </select>
                    <button
                        onClick={fetchProducts}
                        className="px-4 py-2 bg-[var(--accent)] text-[var(--accent-contrast)] rounded-lg hover:opacity-90"
                    >
                        Search
                    </button>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                <div className="card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse text-[var(--text)]">
                            <thead className="bg-[var(--surface)]">
                                <tr>
                                    <th className="text-left p-4 text-sm font-semibold text-[var(--muted)]">Product</th>
                                    <th className="text-left p-4 text-sm font-semibold text-[var(--muted)]">Price</th>
                                    <th className="text-center p-4 text-sm font-semibold text-[var(--muted)]">Status</th>
                                    <th className="text-center p-4 text-sm font-semibold text-[var(--muted)]">Type</th>
                                    <th className="text-center p-4 text-sm font-semibold text-[var(--muted)]">Created</th>
                                    <th className="text-center p-4 text-sm font-semibold text-[var(--muted)]">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((product) => (
                                    <tr key={product.productID} className="border-t border-[var(--border)] hover:bg-[var(--surface)]">
                                        <td className="p-4">
                                            <div className="flex items-center space-x-3">
                                                <img
                                                    src={product.featuredImage?.[0]?.imgUrl || '/placeholder-product.jpg'}
                                                    alt={product.name}
                                                    className="w-12 h-12 object-cover rounded"
                                                />
                                                <div>
                                                    <div className="font-medium text-[var(--text)]">{product.name}</div>
                                                    <div className="text-sm text-[var(--muted)] max-w-xs truncate">
                                                        {product.description}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="text-sm">
                                                <div className="font-medium">{formatPrice(product.salePrice || product.regularPrice)}</div>
                                                {product.salePrice && product.salePrice !== product.regularPrice && (
                                                    <div className="text-xs text-[var(--muted)] line-through">
                                                        {formatPrice(product.regularPrice)}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-center p-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(product.status)}`}>
                                                {product.status}
                                            </span>
                                        </td>
                                        <td className="text-center p-4">
                                            <span className="bg-[var(--surface)] px-2 py-1 rounded text-sm">
                                                {product.type}
                                            </span>
                                        </td>
                                        <td className="text-center p-4 text-sm text-[var(--muted)]">
                                            {formatDate(product.createdAt)}
                                        </td>
                                        <td className="text-center p-4">
                                            <div className="flex items-center justify-center gap-2">
                                                <Link
                                                    to={`/products/${product.productID}`}
                                                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                >
                                                    View
                                                </Link>
                                                <Link
                                                    to={`/products/edit/${product.productID}`}
                                                    className="text-green-600 hover:text-green-800 text-sm font-medium"
                                                >
                                                    Edit
                                                </Link>
                                                <button
                                                    onClick={() => handleDelete(product.productID)}
                                                    disabled={!!deletingId}
                                                    className={`text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50 ${deletingId ? 'cursor-not-allowed' : ''}`}
                                                >
                                                    {deletingId === product.productID ? 'Deleting…' : 'Delete'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && products?.length === 0 && (
                                    <tr>
                                        <td colSpan={6}>
                                            <div className="text-center py-8 text-lg text-[var(--muted)]">
                                                🚫 No Products Found
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination */}
                {!loading && pagination.totalPages > 1 && (
                    <SimplePagination
                        currentPage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                        hasNext={pagination.hasNext}
                        hasPrev={pagination.hasPrev}
                    />
                )}
            </div>
        </div>
    );
}

export default ProductList;
